import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Trash2 } from "lucide-react";

// -------- Utils : dates FR/CH --------
const SWISS_DAYS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const SWISS_MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function parseIsoDate(iso) {
  const [y, m, d] = (iso || "").split("-").map((x) => parseInt(x, 10));
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
}
function toIso(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
function defaultLabelFromIso(iso) {
  const dt = parseIsoDate(iso);
  const dow = SWISS_DAYS[dt.getUTCDay()];
  const day = dt.getUTCDate();
  const month = SWISS_MONTHS[dt.getUTCMonth()];
  return `${dow.charAt(0).toUpperCase() + dow.slice(1)} ${day} ${month}`;
}

// -------- Tabs --------
const TABS = [
  { id: "reservations", label: "Réservations", icon: "📅" },
  { id: "features", label: "Fonctionnalités", icon: "✨" },
  { id: "navigation", label: "Navigation", icon: "🧭" },
  { id: "texts", label: "Textes", icon: "📝" },
  { id: "labels", label: "Labels", icon: "🏷️" },
  { id: "infos", label: "Infos", icon: "ℹ️" },
  { id: "restoPopup", label: "Popup resto", icon: "🍔" },
  { id: "types", label: "Type", icon: "🎨" },
  { id: "lieux", label: "Lieu", icon: "📍" },
];

const SIMPLE_ENDPOINT = {
  reservations: "/admin/config/reservations",
  features: "/admin/config/features",
  navigation: "/admin/config/navigation",
  texts: "/admin/config/texts",
  labels: "/admin/config/labels",
  infos: "/admin/config/infos",
  restoPopup: "/admin/config/resto-popup",
};

const SECTIONS = ["programme", "ateliers", "restauration"];
const SECTION_META = {
  programme: { label: "Programme", emoji: "🎪" },
  ateliers: { label: "Ateliers", emoji: "🎨" },
  restauration: { label: "Restauration", emoji: "🍔" },
};

// ==================== DATES SUBPANEL ====================
function DatesEditor() {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getUTCFullYear());
  const [viewMonth, setViewMonth] = useState(today.getUTCMonth());

  useEffect(() => {
    api.get("/admin/config/dates").then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      setItems(list);
      // Center calendar on first selected date if any
      if (list.length > 0) {
        const dt = parseIsoDate(list[0].iso);
        setViewYear(dt.getUTCFullYear());
        setViewMonth(dt.getUTCMonth());
      }
    }).catch(() => setItems([]));
  }, []);

  const isSelected = (iso) => (items || []).some((it) => it.iso === iso);

  const toggleDay = (iso) => {
    setItems((cur) => {
      const exists = cur.find((c) => c.iso === iso);
      if (exists) return cur.filter((c) => c.iso !== iso);
      return [...cur, { iso, label: defaultLabelFromIso(iso) }].sort((a, b) => a.iso.localeCompare(b.iso));
    });
  };

  const removeItem = (iso) => setItems((cur) => cur.filter((c) => c.iso !== iso));
  const updateLabel = (iso, label) => setItems((cur) => cur.map((c) => c.iso === iso ? { ...c, label } : c));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/config/dates", { items: items.map(({ iso, label }) => ({ iso, label: (label || defaultLabelFromIso(iso)).trim() })) });
      toast.success("Dates enregistrées");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear((y) => y - 1); setViewMonth(11); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear((y) => y + 1); setViewMonth(0); }
    else setViewMonth((m) => m + 1);
  };

  // Build weeks matrix (Monday-first)
  const grid = useMemo(() => {
    const first = new Date(Date.UTC(viewYear, viewMonth, 1));
    const firstDow = (first.getUTCDay() + 6) % 7; // Monday=0
    const daysInMonth = new Date(Date.UTC(viewYear, viewMonth + 1, 0)).getUTCDate();
    const cells = [];
    // Prev-month tails
    const prevDaysInMonth = new Date(Date.UTC(viewYear, viewMonth, 0)).getUTCDate();
    for (let i = firstDow - 1; i >= 0; i--) {
      const day = prevDaysInMonth - i;
      const m = viewMonth === 0 ? 11 : viewMonth - 1;
      const y = viewMonth === 0 ? viewYear - 1 : viewYear;
      cells.push({ iso: toIso(y, m, day), day, inMonth: false });
    }
    for (let d = 1; d <= daysInMonth; d++) cells.push({ iso: toIso(viewYear, viewMonth, d), day: d, inMonth: true });
    // Fill last row to 42
    let d = 1;
    while (cells.length < 42) {
      const m = viewMonth === 11 ? 0 : viewMonth + 1;
      const y = viewMonth === 11 ? viewYear + 1 : viewYear;
      cells.push({ iso: toIso(y, m, d), day: d, inMonth: false });
      d += 1;
    }
    return cells;
  }, [viewYear, viewMonth]);

  if (!items) return <Loader />;

  const monthName = new Date(Date.UTC(viewYear, viewMonth, 1)).toLocaleString("fr-CH", { month: "long", year: "numeric" });

  return (
    <div>
      <div className="text-sm font-bold flex items-center gap-2">📆 Dates du festival</div>
      <div className="text-xs text-neutral-500 mb-3">Touchez les jours du festival dans le calendrier. Chaque date crée un libellé (modifiable) utilisé dans le Programme.</div>

      {/* Calendar */}
      <div className="bg-neutral-950 border border-neutral-800 rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <button onClick={prevMonth} className="p-2 hover:bg-neutral-800 rounded-lg"><ChevronLeft size={18} /></button>
          <div className="font-bold capitalize">{monthName}</div>
          <button onClick={nextMonth} className="p-2 hover:bg-neutral-800 rounded-lg"><ChevronRight size={18} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-neutral-500 uppercase mb-1">
          {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => (<div key={d}>{d}</div>))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {grid.map((c) => {
            const sel = isSelected(c.iso);
            return (
              <button
                key={c.iso}
                type="button"
                onClick={() => toggleDay(c.iso)}
                className={`aspect-square rounded-lg text-sm font-semibold transition-colors ${
                  sel ? "bg-[#A1C861] text-[#0a0a0b]" :
                  c.inMonth ? "hover:bg-neutral-800 text-neutral-200" : "text-neutral-600 hover:bg-neutral-900"
                }`}
              >
                {c.day}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected dates list */}
      <div className="text-xs text-neutral-400 mb-2">Jours sélectionnés ({items.length})</div>
      {items.length === 0 && <div className="text-sm text-neutral-500 mb-3">Aucun jour sélectionné pour l'instant.</div>}
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.iso} className="flex items-center gap-2 bg-neutral-900 border border-neutral-800 rounded-lg p-1 pl-2">
            <span className="chip" style={{ background: "#5BC5F222", color: "#5BC5F2", border: "1px solid #5BC5F255" }}>{it.iso}</span>
            <input
              value={it.label || ""}
              onChange={(e) => updateLabel(it.iso, e.target.value)}
              placeholder={defaultLabelFromIso(it.iso)}
              className="flex-1 bg-transparent outline-none text-sm px-2 py-1"
            />
            <button onClick={() => removeItem(it.iso)} className="p-2 text-red-400 hover:bg-red-900/40 rounded-lg" title="Supprimer">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      <button disabled={saving} onClick={save} className="brand-gradient-btn w-full mt-4 py-3 rounded-lg" style={{ background: "linear-gradient(135deg, #A1C861 0%, #5BC5F2 100%)" }}>
        {saving ? "…" : "Enregistrer les dates"}
      </button>
    </div>
  );
}

// ==================== TYPES / LIEUX SUBPANEL (generic) ====================
function ListEditor({ kind /* "types" | "lieux" */ }) {
  const [section, setSection] = useState("programme");
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // {id?, name, colorHex?}

  const load = async () => {
    setItems(null);
    const { data } = await api.get(`/admin/config/${kind}/${section}`);
    setItems(data);
  };
  useEffect(() => { load().catch(() => setItems([])); }, [kind, section]); // eslint-disable-line

  const startNew = () => setEditing({ name: "", colorHex: "#A1C861" });
  const startEdit = (it) => setEditing({ ...it });

  const save = async () => {
    try {
      const body = { name: (editing.name || "").trim() };
      if (kind === "lieux") body.colorHex = editing.colorHex || "#A1C861";
      if (kind === "types" && editing.colorHex) body.colorHex = editing.colorHex;
      if (!body.name) { toast.error("Nom requis"); return; }
      if (editing.id) await api.put(`/admin/config/${kind}/${section}/${editing.id}`, body);
      else await api.post(`/admin/config/${kind}/${section}`, body);
      toast.success("Enregistré"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async (it) => {
    if (!window.confirm(`Supprimer "${it.name}" ?`)) return;
    try { await api.delete(`/admin/config/${kind}/${section}/${it.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {SECTIONS.map((s) => (
          <button key={s} onClick={() => setSection(s)} className={`px-3 py-2 rounded-lg text-sm font-semibold ${section === s ? "bg-neutral-800 text-white" : "bg-neutral-900 text-neutral-400 hover:bg-neutral-800"}`}>
            {SECTION_META[s].emoji} {SECTION_META[s].label}
          </button>
        ))}
      </div>

      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-neutral-400">
          {kind === "types" ? "Types utilisables dans les fiches" : "Lieux (chaque lieu porte une couleur)"} — {SECTION_META[section].label}
        </div>
        <button onClick={startNew} className="brand-gradient-btn px-3 py-1.5 rounded-lg text-sm">+ Ajouter</button>
      </div>

      {!items ? <Loader /> : items.length === 0 ? (
        <div className="text-sm text-neutral-500">Aucun {kind === "types" ? "type" : "lieu"} pour cette section.</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id || it.name} className="card-hsg p-3 flex items-center gap-3">
              <span className="inline-block w-4 h-4 rounded-full border border-black/30 shrink-0" style={{ background: it.colorHex || (kind === "types" ? "#5BC5F2" : "#A1C861") }} />
              <div className="flex-1 min-w-0 truncate">{it.name}</div>
              {it.id && (
                <>
                  <button onClick={() => startEdit(it)} className="px-3 py-1 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-sm">✏️</button>
                  <button onClick={() => del(it)} className="px-3 py-1 rounded-lg bg-red-900/40 hover:bg-red-900/70 text-sm">🗑️</button>
                </>
              )}
              {!it.id && <span className="text-[10px] text-neutral-500 italic">défaut</span>}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="card-hsg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-3">{editing.id ? "Modifier" : "Nouveau"} — {kind === "types" ? "type" : "lieu"} ({SECTION_META[section].label})</h3>
            <label className="block">
              <span className="text-xs uppercase text-neutral-400">Nom</span>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1 w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" />
            </label>
            <label className="block mt-3">
              <span className="text-xs uppercase text-neutral-400">Couleur (hex){kind === "types" ? " — optionnelle" : ""}</span>
              <div className="mt-1 flex gap-2 items-center">
                <input type="color" value={editing.colorHex || "#A1C861"} onChange={(e) => setEditing({ ...editing, colorHex: e.target.value })} className="w-14 h-10 bg-neutral-900 border border-neutral-700 rounded-lg" />
                <input value={editing.colorHex || ""} onChange={(e) => setEditing({ ...editing, colorHex: e.target.value })} placeholder="#A1C861" className="flex-1 bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2 font-mono text-sm" />
              </div>
            </label>
            <div className="flex gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="flex-1 py-2 rounded-lg bg-neutral-800 hover:bg-neutral-700">Annuler</button>
              <button onClick={save} className="flex-1 brand-gradient-btn py-2 rounded-lg">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== NAVIGATION EDITOR (with maintenance & vacation switches + ordering) ====================
const NAV_ITEMS = [
  { key: "showAccueil", orderKey: "accueil", label: "Accueil", icon: "🏠" },
  { key: "showProgramme", orderKey: "programme", label: "Programme", icon: "📅" },
  { key: "showPlan", orderKey: "plan", label: "Plan", icon: "📍" },
  { key: "showAteliers", orderKey: "ateliers", label: "Ateliers", icon: "♟️" },
  { key: "showResto", orderKey: "resto", label: "Resto", icon: "🍽️" },
  { key: "showBoutique", orderKey: "boutique", label: "Boutique", icon: "🛒" },
  { key: "showInfos", orderKey: "infos", label: "Infos", icon: "ℹ️" },
];

function NavigationEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/config/navigation").then(({ data }) => setData(data)).catch(() => setData({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/config/navigation", data); toast.success("Enregistré"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  if (!data) return <Loader />;
  const maintenance = !!data.maintenance;
  const vacation = !!data.vacation;
  const order = data.order || {};

  // Only maintenance disables per-tab checkboxes (vacation is a "soft" preset)
  const perTabDisabled = maintenance;

  // Sort tabs by user-defined order for display
  const orderedNavItems = [...NAV_ITEMS].sort(
    (a, b) => (Number(order[a.orderKey]) || 99) - (Number(order[b.orderKey]) || 99)
  );

  const toggleVacation = () => {
    // Turning vacation ON: pre-fill Infos + Boutique visible, others hidden (user can still adjust)
    if (!vacation) {
      setData({
        ...data,
        vacation: true,
        maintenance: false,
        showAccueil: false,
        showProgramme: false,
        showPlan: false,
        showAteliers: false,
        showResto: false,
        showBoutique: true,
        showInfos: true,
      });
    } else {
      setData({ ...data, vacation: false });
    }
  };

  const updateOrder = (key, val) => {
    const n = Math.max(1, Math.min(NAV_ITEMS.length, parseInt(val, 10) || 1));
    setData({ ...data, order: { ...order, [key]: n } });
  };

  return (
    <div className="space-y-4">
      {/* Maintenance toggle */}
      <div className="rounded-xl p-4 flex items-center gap-3" style={{ background: "#141416", border: "1px solid #23232a" }}>
        <div className="text-3xl">🛠️</div>
        <div className="flex-1">
          <div className="font-bold">Mode maintenance</div>
          <div className="text-xs text-neutral-400">Masque tous les onglets sauf Accueil. Le bouton "Découvrir le festival" est désactivé (clic sans effet).</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={maintenance}
          onClick={() => setData({ ...data, maintenance: !maintenance, vacation: !maintenance ? false : data.vacation })}
          className={`w-12 h-7 rounded-full transition-colors relative ${maintenance ? "bg-[#A1C861]" : "bg-neutral-700"}`}
          data-testid="toggle-maintenance"
        >
          <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${maintenance ? "translate-x-5" : ""}`} />
        </button>
      </div>

      {/* Vacation toggle */}
      <div className="rounded-xl p-4 flex items-center gap-3" style={{ background: "#141416", border: "1px solid #23232a" }}>
        <div className="text-3xl">🏖️</div>
        <div className="flex-1">
          <div className="font-bold">Mode vacances</div>
          <div className="text-xs text-neutral-400">Pré-configure l'affichage : seuls Infos et Boutique sont visibles par défaut. L'onglet Infos utilise l'icône 🏠 (maison) à la place de ℹ️. Vous pouvez toujours ajuster manuellement les onglets ci-dessous.</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={vacation}
          onClick={toggleVacation}
          className={`w-12 h-7 rounded-full transition-colors relative ${vacation ? "bg-[#FEC900]" : "bg-neutral-700"}`}
          data-testid="toggle-vacation"
        >
          <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${vacation ? "translate-x-5" : ""}`} />
        </button>
      </div>

      <div className="text-xs text-neutral-500 italic">
        Cochez les onglets à afficher dans la barre de menu du bas. Utilisez le numéro pour définir l'ordre d'affichage (1 = premier à gauche).
      </div>

      {/* Per-tab toggles + order input */}
      <div className="space-y-2">
        {orderedNavItems.map((n) => {
          const on = !!data[n.key];
          const current = Number(order[n.orderKey]) || NAV_ITEMS.findIndex((x) => x.orderKey === n.orderKey) + 1;
          // In vacation mode, the Infos tab is displayed with a house icon on the mobile app
          const displayIcon = (vacation && n.orderKey === "infos") ? "🏠" : n.icon;
          return (
            <div key={n.key} className={`flex items-center gap-3 p-3 rounded-lg border transition-opacity ${perTabDisabled ? "opacity-50" : ""} ${on ? "bg-[#A1C86122] border-[#A1C86155]" : "bg-neutral-900 border-neutral-800"}`}>
              <label className={`flex items-center gap-3 flex-1 ${perTabDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
                <input type="checkbox" checked={on} disabled={perTabDisabled} onChange={(e) => setData({ ...data, [n.key]: e.target.checked })} className="w-5 h-5 accent-[#A1C861]" />
                <span className="text-lg">{displayIcon}</span>
                <span className="font-semibold">{n.label}{vacation && n.orderKey === "infos" && <span className="ml-2 text-[10px] text-[#FEC900] uppercase">(icône maison en mode vacances)</span>}</span>
              </label>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] uppercase text-neutral-500">Ordre</span>
                <input
                  type="number"
                  min={1}
                  max={NAV_ITEMS.length}
                  value={current}
                  disabled={perTabDisabled}
                  onChange={(e) => updateOrder(n.orderKey, e.target.value)}
                  className="w-14 bg-neutral-950 border border-neutral-700 rounded-lg px-2 py-1 text-center text-sm disabled:opacity-50"
                  data-testid={`order-${n.orderKey}`}
                />
              </div>
            </div>
          );
        })}
      </div>

      <button disabled={saving} onClick={save} className="brand-gradient-btn px-6 py-3 rounded-lg" data-testid="save-navigation">
        {saving ? "…" : "Enregistrer"}
      </button>
    </div>
  );
}

// ==================== SIMPLE KEY/VALUE EDITOR (used for the pre-existing tabs) ====================
function SimpleConfigEditor({ tabId }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const ep = SIMPLE_ENDPOINT[tabId];
  const load = async () => { setData(null); const { data } = await api.get(ep); setData(data); };
  useEffect(() => { load(); }, [tabId]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try { await api.put(ep, data); toast.success("Enregistré"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  if (!data) return <Loader />;

  return (
    <div>
      {tabId === "reservations" && (
        <>
          <Row label="Fermeture (minutes avant le début du créneau)" hint="Le créneau devient 'fermé' N minutes avant son début.">
            <input type="number" min="0" max="240" value={data.cutoffMinutes || 0} onChange={(e) => setData({ ...data, cutoffMinutes: Number(e.target.value) })} className="w-32 bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" />
          </Row>
          <Row label="Ouverture (jours avant le 1er créneau du jour)">
            <input type="number" min="0" max="365" value={data.openDaysBeforeFirst || 0} onChange={(e) => setData({ ...data, openDaysBeforeFirst: Number(e.target.value) })} className="w-32 bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" />
          </Row>
          <Row label="Message d'ouverture">
            <input value={data.openMessage || ""} onChange={(e) => setData({ ...data, openMessage: e.target.value })} className="w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" />
          </Row>
        </>
      )}
      {tabId === "features" && ["showBilletterie", "showBracelet", "showBoutique"].map((k) => (
        <Row key={k} label={k}>
          <ToggleCheckbox on={!!data[k]} onChange={(v) => setData({ ...data, [k]: v })} />
        </Row>
      ))}
      {(tabId === "texts" || tabId === "labels" || tabId === "infos" || tabId === "restoPopup") && Object.keys(data).map((k) => (
        <Row key={k} label={k}>
          {(k === "body" || k === "subtitle" || k === "description") ? (
            <textarea rows={3} value={data[k] || ""} onChange={(e) => setData({ ...data, [k]: e.target.value })} className="w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" />
          ) : (
            <input value={data[k] || ""} onChange={(e) => setData({ ...data, [k]: e.target.value })} className="w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" />
          )}
        </Row>
      ))}
      <div className="pt-4">
        <button onClick={save} disabled={saving} className="brand-gradient-btn px-6 py-2 rounded-lg">{saving ? "…" : "Enregistrer"}</button>
      </div>
    </div>
  );
}

function Row({ label, children, hint }) {
  return (
    <div className="py-3 border-b border-neutral-800 last:border-0">
      <div className="text-sm font-semibold">{label}</div>
      {hint && <div className="text-xs text-neutral-500 mb-2">{hint}</div>}
      <div className="mt-1">{children}</div>
    </div>
  );
}
function ToggleCheckbox({ on, onChange }) {
  return (
    <label className="inline-flex items-center gap-2">
      <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} />
      <span>{on ? "Activé" : "Désactivé"}</span>
    </label>
  );
}

// ==================== INFOS TAB WRAPPER (includes Dates) ====================
function InfosTabPanel() {
  return (
    <div className="space-y-6">
      <SimpleConfigEditor tabId="infos" />
      <div className="pt-6 border-t border-neutral-800">
        <DatesEditor />
      </div>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================
export default function AdminPersonalization() {
  const [tab, setTab] = useState("reservations");
  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <h1 className="text-3xl font-black">Personnalisation</h1>
      <p className="text-neutral-400 text-sm">Ajustez les textes, réglages, dates et fonctionnalités du festival</p>

      <div className="flex flex-wrap gap-2 mt-6 mb-4">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === t.id ? "bg-neutral-800 text-white" : "bg-neutral-900 text-neutral-400 hover:bg-neutral-800"}`} data-testid={`tab-${t.id}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="card-hsg p-6">
        {tab === "navigation" && <NavigationEditor />}
        {tab === "infos" && <InfosTabPanel />}
        {tab === "types" && <ListEditor kind="types" />}
        {tab === "lieux" && <ListEditor kind="lieux" />}
        {(tab === "reservations" || tab === "features" || tab === "texts" || tab === "labels" || tab === "restoPopup") && (
          <SimpleConfigEditor tabId={tab} />
        )}
      </div>
    </div>
  );
}
